import './settings-leaf.css';
import { useTranslation } from 'react-i18next';
import { Select } from '../ui.tsx';
import { QUOTE_FOLLOW_UP_DAY_CHOICES } from '@shared/constants/index.ts';
import { CardSaveFooter, useCompanyCardForm } from './company-card-form.tsx';
import type { Company } from '@shared/types/index.ts';

/**
 * The two automatic emails that chase a quote on the customer's side: the
 * nudge when nothing has been answered, and the warning before the price
 * lapses. What the app tells the company's own people is the Notifications
 * card — a different audience, deliberately a different card.
 */

const snapshot = (company: Company) => ({
  quoteFollowUpDays: company.quoteFollowUpDays,
  quoteExpiryWarningDays: company.quoteExpiryWarningDays,
});

const toPayload = (draft: ReturnType<typeof snapshot>) => ({
  quoteFollowUpDays: draft.quoteFollowUpDays,
  quoteExpiryWarningDays: draft.quoteExpiryWarningDays,
});

export default function FollowUpsCard() {
  const { t } = useTranslation();
  const form = useCompanyCardForm(snapshot, toPayload);

  const draft = form.draft;
  if (!draft) return null;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="follow-ups-heading">
      <div className="work-section-heading">
        <h2 id="follow-ups-heading" className="mb-2 text-lg font-semibold">
          {t('settings.followUpsHeading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.followUpsDescription')}</p>

      <div className="grid gap-x-4 sm:grid-cols-2">
        <Select
          label={t('settings.followUpNudge')}
          hint={t('settings.followUpNudgeHint')}
          value={String(draft.quoteFollowUpDays)}
          disabled={form.readOnly}
          error={form.fieldErrors.quoteFollowUpDays}
          options={QUOTE_FOLLOW_UP_DAY_CHOICES.map((days) => ({
            value: String(days),
            label: days === 0 ? t('settings.followUpOff') : t('settings.followUpNudgeAfter', { count: days }),
          }))}
          onChange={(event) => form.set('quoteFollowUpDays', Number(event.target.value))}
        />
        <Select
          label={t('settings.expiryWarning')}
          hint={t('settings.expiryWarningHint')}
          value={String(draft.quoteExpiryWarningDays)}
          disabled={form.readOnly}
          error={form.fieldErrors.quoteExpiryWarningDays}
          options={QUOTE_FOLLOW_UP_DAY_CHOICES.map((days) => ({
            value: String(days),
            label: days === 0 ? t('settings.followUpOff') : t('settings.expiryWarningBefore', { count: days }),
          }))}
          onChange={(event) => form.set('quoteExpiryWarningDays', Number(event.target.value))}
        />
      </div>

      <CardSaveFooter form={form} saveLabel={t('settings.saveFollowUps')} />
    </section>
  );
}
